// ONBOARDING-P3: the case detail's Proposal tab. The version under review (the draft, or the
// latest version when every one was sent: saving then opens a new version to review) as simple
// form fields, lists one per line. Save = `operator.onboarding.proposal.edit` (a 422 lists what
// the validator refused), Send to client = `.send` (freezes the draft). Older versions read-only.
import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../ops-parts";
import { Section } from "../screen-parts";
import { when } from "./onboarding-labels";

type View = OperationOutput<"operator.onboarding.proposal.get">;
type Version = View["versions"][number];
type Body = Version["body"];
type Seat = Body["seats"][number];

export const proposalKey = (companyId: string) => ["operator", "onboarding", "proposal", companyId];

type SeatDraft = {
  key: string;
  title: string;
  kind: Seat["kind"];
  template_key: string;
  helpers: string;
  kpis: string;
  first_goals: string;
  start: Seat["start"];
  evidence: string;
};
type Draft = { summary: string; seats: SeatDraft[]; first_goals: string; open_questions: string };

const lines = (s: string) =>
  s
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

function toDraft(b: Body): Draft {
  return {
    summary: b.summary,
    seats: b.seats.map((s) => ({
      key: s.key,
      title: s.title,
      kind: s.kind,
      template_key: s.template_key ?? "",
      helpers: s.helpers.join("\n"),
      kpis: s.kpis.join("\n"),
      first_goals: s.first_goals.join("\n"),
      start: s.start,
      evidence: s.evidence.join("\n"),
    })),
    first_goals: b.first_goals.join("\n"),
    open_questions: b.open_questions.join("\n"),
  };
}

function toBody(d: Draft): Body {
  return {
    summary: d.summary.trim(),
    seats: d.seats.map((s) => ({
      key: s.key.trim(),
      title: s.title.trim(),
      kind: s.kind,
      template_key: s.template_key.trim() || null,
      helpers: lines(s.helpers),
      kpis: lines(s.kpis),
      first_goals: lines(s.first_goals),
      start: s.start,
      evidence: lines(s.evidence),
    })),
    first_goals: lines(d.first_goals),
    open_questions: lines(d.open_questions),
  };
}

/** The version the operator works on: the draft, else the latest one. */
export function underReview(v: View): Version | undefined {
  return (
    v.versions.find((x) => x.status === "draft") ??
    [...v.versions].sort((a, b) => b.version - a.version)[0]
  );
}

function Problems({ error }: { error: unknown }) {
  const errors = error instanceof ApiError ? (error.problem?.errors ?? []) : [];
  return (
    <Notice tone="danger" role="alert">
      <p>{problemText(error, "The proposal could not be saved.")}</p>
      {errors.length ? (
        <ul className="mt-1 list-disc pl-5">
          {errors.map((e) => (
            <li key={`${e.path}:${e.code}`}>
              {e.path ? `${e.path}: ` : ""}
              {e.message}
            </li>
          ))}
        </ul>
      ) : null}
    </Notice>
  );
}

function ListField({
  id,
  label,
  value,
  onChange,
  rows = 3,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SeatFields({
  seat,
  n,
  onChange,
  onRemove,
}: {
  seat: SeatDraft;
  n: number;
  onChange: (s: SeatDraft) => void;
  onRemove: () => void;
}) {
  const id = (f: string) => `seat-${n}-${f}`;
  const set = (patch: Partial<SeatDraft>) => onChange({ ...seat, ...patch });
  return (
    <fieldset
      className="flex flex-col gap-3 rounded-md border border-border p-3"
      data-seat-form={n}
    >
      <legend className="px-1 text-sm font-medium">
        Seat {n + 1}
        {seat.title ? `: ${seat.title}` : ""}
      </legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={id("title")}>Title</Label>
          <Input
            id={id("title")}
            value={seat.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={id("key")}>Key</Label>
          <Input id={id("key")} value={seat.key} onChange={(e) => set({ key: e.target.value })} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={id("kind")}>Kind</Label>
          <NativeSelect
            id={id("kind")}
            value={seat.kind}
            onChange={(e) => set({ kind: e.target.value as Seat["kind"] })}
          >
            <NativeSelectOption value="de">Digital employee</NativeSelectOption>
            <NativeSelectOption value="human">Person</NativeSelectOption>
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={id("start")}>Start</Label>
          <NativeSelect
            id={id("start")}
            value={seat.start}
            onChange={(e) => set({ start: e.target.value as Seat["start"] })}
          >
            <NativeSelectOption value="now">Included now</NativeSelectOption>
            <NativeSelectOption value="on_upgrade">On an upgrade</NativeSelectOption>
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <Label htmlFor={id("template")}>Template key (empty: none fits, a ticket)</Label>
          <Input
            id={id("template")}
            value={seat.template_key}
            onChange={(e) => set({ template_key: e.target.value })}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ListField
          id={id("helpers")}
          label="Helpers (one per line)"
          value={seat.helpers}
          onChange={(v) => set({ helpers: v })}
        />
        <ListField
          id={id("kpis")}
          label="KPIs (one per line)"
          value={seat.kpis}
          onChange={(v) => set({ kpis: v })}
        />
        <ListField
          id={id("goals")}
          label="First goals (one per line)"
          value={seat.first_goals}
          onChange={(v) => set({ first_goals: v })}
        />
        <ListField
          id={id("evidence")}
          label="Evidence: intake fields (one per line)"
          value={seat.evidence}
          onChange={(v) => set({ evidence: v })}
        />
      </div>
      <div>
        <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
          Remove seat
        </Button>
      </div>
    </fieldset>
  );
}

function ReadOnlyVersion({ v }: { v: Version }) {
  return (
    <details className="rounded-md border border-border px-3 py-2" data-version={v.version}>
      <summary className="cursor-pointer text-sm">
        Version {v.version} · {v.status}
        {v.sent_at ? ` · sent ${when(v.sent_at)}` : ""}
      </summary>
      <div className="mt-2 flex flex-col gap-2 text-sm">
        <p>{v.body.summary}</p>
        <ul className="list-disc pl-5">
          {v.body.seats.map((s) => (
            <li key={s.key}>
              {s.title} ({s.kind === "de" ? "DE" : "person"}
              {s.template_key ? `, ${s.template_key}` : ""},{" "}
              {s.start === "now" ? "now" : "on upgrade"})
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

export function ProposalEditor({
  api,
  companyId,
  view,
}: {
  api: Api;
  companyId: string;
  view: View;
}) {
  const qc = useQueryClient();
  const current = underReview(view);
  const [draft, setDraft] = useState<Draft | null>(null);
  const base = current ? toDraft(current.body) : null;
  const d = draft ?? base;
  const save = useMutation({
    mutationFn: (body: Body) =>
      api.client.operator.onboarding.proposal.edit({ params: { company_id: companyId }, body }),
    onSuccess: (v) => {
      qc.setQueryData(proposalKey(companyId), v);
      setDraft(null);
    },
  });
  const send = useMutation({
    mutationFn: () =>
      api.client.operator.onboarding.proposal.send({ params: { company_id: companyId } }),
    onSuccess: (v) => qc.setQueryData(proposalKey(companyId), v),
  });
  if (!current || !d)
    return (
      <p className="text-sm text-muted-foreground">
        No proposal yet: it is drafted once the owner completes the intake.
      </p>
    );
  const update = (patch: Partial<Draft>) => setDraft({ ...d, ...patch });
  const older = view.versions.filter((v) => v.id !== current.id);
  const dirty = draft !== null;
  return (
    <div className="flex flex-col gap-4" data-slot="proposal-editor">
      <p className="text-sm text-muted-foreground">
        Version {current.version} ·{" "}
        <Badge variant={current.status === "draft" ? "warning" : "neutral"}>{current.status}</Badge>
        {current.status === "draft"
          ? " · the client sees it only once you send it"
          : " · saving opens a new version to review"}
        {current.draft_note ? ` · ${current.draft_note}` : ""}
      </p>
      <form
        className="flex flex-col gap-4"
        aria-label="Setup proposal"
        onSubmit={(e) => {
          e.preventDefault();
          send.reset();
          save.mutate(toBody(d));
        }}
      >
        <ListField
          id="proposal-summary"
          label="Summary"
          rows={4}
          value={d.summary}
          onChange={(v) => update({ summary: v })}
        />
        {d.seats.map((s, i) => (
          <SeatFields
            // biome-ignore lint/suspicious/noArrayIndexKey: seats are edited in place, keys change
            key={i}
            n={i}
            seat={s}
            onChange={(next) => update({ seats: d.seats.map((x, j) => (j === i ? next : x)) })}
            onRemove={() => update({ seats: d.seats.filter((_, j) => j !== i) })}
          />
        ))}
        <div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() =>
              update({
                seats: [
                  ...d.seats,
                  {
                    key: "",
                    title: "",
                    kind: "de",
                    template_key: "",
                    helpers: "",
                    kpis: "",
                    first_goals: "",
                    start: "now",
                    evidence: "",
                  },
                ],
              })
            }
          >
            Add seat
          </Button>
        </div>
        <ListField
          id="proposal-goals"
          label="First goals (one per line)"
          value={d.first_goals}
          onChange={(v) => update({ first_goals: v })}
        />
        <ListField
          id="proposal-questions"
          label="Open questions (one per line)"
          value={d.open_questions}
          onChange={(v) => update({ open_questions: v })}
        />
        {save.isError ? <Problems error={save.error} /> : null}
        {send.isError ? <Problems error={send.error} /> : null}
        {send.isSuccess ? (
          <Notice tone="success" role="status">
            Sent to the client as version {send.data.sent_version}.
          </Notice>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="secondary" disabled={save.isPending}>
            Save
          </Button>
          <Button
            type="button"
            disabled={send.isPending || dirty || current.status !== "draft"}
            onClick={() => {
              save.reset();
              send.mutate();
            }}
          >
            Send to client
          </Button>
          {dirty ? (
            <span className="self-center text-xs text-muted-foreground">
              Save your changes before sending.
            </span>
          ) : null}
        </div>
      </form>
      {older.length ? (
        <Section id="proposal-older" title="Other versions">
          {older
            .sort((a, b) => b.version - a.version)
            .map((v) => (
              <ReadOnlyVersion key={v.id} v={v} />
            ))}
        </Section>
      ) : null}
    </div>
  );
}
